import { useHqScreenEvent, trackHq } from './analytics';
import { HQ_NAME } from './config';
import type { HqGuide } from './guides';
import { EmailCapture } from './EmailCapture';
import { HqLayout } from './layout';
import { HqLink } from './Link';
import { HqHarborBand, HqKicker, HqSmartLink, trackOutbound } from './ui';
import { harborUrl } from './urls';

function headingId(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function GuidePage({ guide }: { guide: HqGuide }) {
  useHqScreenEvent('HQ Guide View', { guide: guide.slug });

  const canonical = `https://coparentinghq.com/guides/${guide.slug}`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: HQ_NAME, item: 'https://coparentinghq.com/' },
        { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://coparentinghq.com/guides' },
        { '@type': 'ListItem', position: 3, name: guide.title, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: guide.title,
      description: guide.description,
      url: canonical,
      author: { '@type': 'Organization', name: HQ_NAME, url: 'https://coparentinghq.com/' },
      publisher: { '@type': 'Organization', name: 'Harbor', url: 'https://harborcoparent.com' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: guide.title,
      description: guide.description,
      step: guide.checklist.map((item, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        text: item,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: guide.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    },
  ];

  return (
    <HqLayout
      title={`${guide.title}: ${HQ_NAME}`}
      description={guide.description}
      path={`/guides/${guide.slug}`}
      jsonLd={jsonLd}
    >
      <div className='lg:grid lg:grid-cols-[minmax(0,1fr)_19rem] lg:items-start lg:gap-12'>
        <article className='max-w-2xl'>
          <p className='text-sm text-hq-muted'>
            <HqLink to='/' className='hover:text-hq-ink'>
              Home
            </HqLink>
            <span aria-hidden className='px-1.5'>
              /
            </span>
            <HqLink to='/guides' className='hover:text-hq-ink'>
              Guides
            </HqLink>
          </p>
          <HqKicker>Guide · {guide.readMinutes} min</HqKicker>
          <h1 className='mt-2 font-hq text-4xl font-medium tracking-tight sm:text-5xl'>{guide.title}</h1>
          <p className='mt-4 text-lg leading-relaxed text-hq-muted'>{guide.intro}</p>

          <nav className='mt-8 rounded-2xl border border-hq-border bg-hq-paper px-5 py-4' aria-label='On this page'>
            <p className='text-xs font-semibold uppercase tracking-[0.14em] text-hq-legal'>On this page</p>
            <ol className='mt-3 space-y-1.5 text-sm'>
              {guide.sections.map((section) => (
                <li key={section.heading}>
                  <a href={`#${headingId(section.heading)}`} className='text-hq-sage hover:underline'>
                    {section.heading}
                  </a>
                </li>
              ))}
              <li>
                <a href='#checklist' className='text-hq-sage hover:underline'>
                  Checklist
                </a>
              </li>
              <li>
                <a href='#questions' className='text-hq-sage hover:underline'>
                  Questions
                </a>
              </li>
            </ol>
          </nav>

          {guide.sections.map((section) => (
            <section key={section.heading} id={headingId(section.heading)} className='mt-10 scroll-mt-24'>
              <h2 className='font-hq text-2xl font-medium tracking-tight'>{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className='mt-3 leading-relaxed text-hq-body'>
                  {paragraph}
                </p>
              ))}
              {section.bullets ? (
                <ul className='mt-3 list-disc space-y-2 pl-5 text-hq-body'>
                  {section.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}

          <section id='questions' className='mt-12 scroll-mt-24'>
            <h2 className='font-hq text-2xl font-medium tracking-tight'>Questions</h2>
            <dl className='mt-5 space-y-5'>
              {guide.faqs.map((faq) => (
                <div key={faq.question}>
                  <dt className='font-semibold text-hq-ink'>{faq.question}</dt>
                  <dd className='mt-1 leading-relaxed text-hq-muted'>{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </section>

          <p className='mt-10 text-sm text-hq-muted'>
            Next:{' '}
            <HqSmartLink
              href={guide.nextTool.href}
              kind={guide.nextTool.kind}
              className='font-semibold text-hq-sage hover:underline'
              onClick={() =>
                trackOutbound(guide.nextTool.kind, { location: 'guide-next', guide: guide.slug })
              }
            >
              {guide.nextTool.label}
            </HqSmartLink>
          </p>

          {guide.harborNext ? (
            <p className='mt-3 text-sm text-hq-muted'>
              When you want this in one place day to day:{' '}
              <a
                href={harborUrl(guide.harborNext.path)}
                className='font-semibold text-hq-sage hover:underline'
                onClick={() =>
                  trackHq('HQ Harbor CTA Pressed', { location: 'guide-harbor', guide: guide.slug })
                }
              >
                {guide.harborNext.label}
              </a>
              .
            </p>
          ) : null}

          <p className='mt-10 text-xs text-hq-legal'>
            This page is general information, not legal advice, and not a substitute for a lawyer in
            your state.
          </p>
        </article>

        <aside className='mt-12 space-y-4 lg:sticky lg:top-24 lg:mt-0'>
          <section id='checklist' className='rounded-2xl border border-hq-border bg-hq-paper px-5 py-5 scroll-mt-24'>
            <h2 className='font-hq text-xl font-medium'>{guide.outcome}</h2>
            <ol className='mt-4 list-decimal space-y-2.5 pl-5 text-sm text-hq-body'>
              {guide.checklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>
          <EmailCapture guideSlug={guide.slug} guideTitle={guide.title} />
        </aside>
      </div>

      <div className='mt-16'>
        <HqHarborBand location={`guide-band:${guide.slug}`} />
      </div>
    </HqLayout>
  );
}
